import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  BrainCircuit,
  CalendarDays,
  ClipboardList,
  HardHat,
  LayoutDashboard,
  LogOut,
  Map,
  Menu,
  Megaphone,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
  Building2,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { OfflineCenter } from "@/features/offline/offline-center";
import { clearOfflineSession } from "@/features/offline/offline-db";
import { sessionWithOfflineFallback } from "@/features/auth/require-auth";

const links = [
  {
    label: "Inicio",
    to: "/inicio",
    Icon: LayoutDashboard,
    permissions: ["indicators.read.all", "indicators.read.area"],
  },
  {
    label: "Territorio",
    to: "/mapa",
    Icon: Map,
    permissions: ["territory.read"],
  },
  {
    label: "Prioridades",
    to: "/prioridades",
    Icon: SlidersHorizontal,
    permissions: ["territory.read"],
  },
  {
    label: "Levantamientos",
    to: "/levantamientos",
    Icon: ClipboardList,
    permissions: ["needs.read.all", "needs.read.area"],
  },
  {
    label: "Obras y acciones",
    to: "/acciones",
    Icon: HardHat,
    permissions: ["actions.read.all", "actions.read.area"],
  },
  {
    label: "Agenda",
    to: "/agenda",
    Icon: CalendarDays,
    permissions: ["agenda.read.all", "agenda.read.area"],
  },
  {
    label: "Comunicación",
    to: "/comunicacion",
    Icon: Megaphone,
    permissions: ["communication.manage", "communication.approve"],
  },
  {
    label: "Indicadores",
    to: "/indicadores",
    Icon: BarChart3,
    permissions: ["indicators.read.all", "indicators.read.area"],
  },
  {
    label: "Validación ciudadana",
    to: "/validaciones",
    Icon: UsersRound,
    permissions: ["validations.read.all", "validations.read.area"],
  },
  {
    label: "Notificaciones",
    to: "/notificaciones",
    Icon: Bell,
    permissions: ["notifications.read"],
  },
  {
    label: "Analítica e IA",
    to: "/analitica",
    Icon: BrainCircuit,
    permissions: ["analytics.read.all", "analytics.read.area"],
  },
  {
    label: "Dependencias",
    to: "/dependencias",
    Icon: Building2,
    permissions: ["dependencies.read.all", "dependencies.read.area"],
  },
  {
    label: "Estado del sistema",
    to: "/monitoreo",
    Icon: Activity,
    permissions: ["monitoring.read"],
  },
  {
    label: "Configuración",
    to: "/catalogos",
    Icon: Settings,
    permissions: ["catalogs.read.all", "catalogs.read.area"],
  },
  {
    label: "Usuarios y permisos",
    to: "/administracion",
    Icon: UsersRound,
    permissions: ["admin.manage"],
  },
  {
    label: "Auditoría",
    to: "/auditoria",
    Icon: ShieldCheck,
    permissions: ["audit.read"],
  },
] as const;

function Navigation({
  permissions,
  onNavigate,
}: {
  permissions: string[];
  onNavigate?: () => void;
}) {
  return (
    <nav
      className="flex flex-col gap-1 px-3 py-4"
      aria-label="Navegación principal"
    >
      {links
        .filter((item) =>
          item.permissions.some((permission) =>
            permissions.includes(permission),
          ),
        )
        .map(({ label, to, Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex h-10 items-center gap-3 rounded-md px-3 text-[13px] font-medium text-sidebar-foreground/80 transition-colors hover:bg-white/10 hover:text-white",
                isActive &&
                  "bg-sidebar-primary text-sidebar-primary-foreground shadow-md",
              )
            }
          >
            <Icon aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex h-[76px] items-center gap-3 border-b border-white/15 px-5">
      <div className="grid size-10 place-items-center border border-cyan-200/70 text-xl font-bold text-white">
        S
      </div>
      <div>
        <strong className="block text-lg tracking-[0.08em] text-white">
          SISCOM
        </strong>
        <span className="block text-[10px] text-cyan-100/70">
          Los Cabos · B.C.S.
        </span>
      </div>
    </div>
  );
}

export function AppShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useQuery({
    queryKey: ["auth-session"],
    queryFn: sessionWithOfflineFallback,
    staleTime: 60_000,
  });
  const permissions = session.data?.user?.permissions ?? [];
  const user = session.data?.user;
  const active =
    links.find((item) => location.pathname.startsWith(item.to))?.label ??
    "SISCOM";
  const initials =
    user?.fullName
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() ?? "SC";
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: async () => {
      await clearOfflineSession();
      client.setQueryData(["auth-session"], {
        authenticated: false,
        mode: "required",
        user: null,
      });
      navigate("/acceso", { replace: true });
    },
  });
  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[224px_minmax(0,1fr)]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col bg-sidebar lg:flex">
        <Brand />
        <Navigation permissions={permissions} />
        <div className="mt-auto border-t border-white/10 px-6 py-5 text-[11px] text-sidebar-foreground/65">
          <span className="block">Coordinación municipal</span>
          <span className="mt-1 block">Uso interno · v1.0</span>
        </div>
      </aside>
      <main className="min-w-0 lg:col-start-2">
        <header className="sticky top-0 z-20 flex h-[76px] items-center justify-between border-b bg-card/95 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Abrir navegación"
                >
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 border-0 bg-sidebar p-0 text-sidebar-foreground"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Navegación SISCOM</SheetTitle>
                </SheetHeader>
                <Brand />
                <Navigation
                  permissions={permissions}
                  onNavigate={() => setOpen(false)}
                />
              </SheetContent>
            </Sheet>
            <div>
              <h1 className="text-xl font-bold tracking-tight md:text-2xl">
                {active}
              </h1>
              <p className="hidden text-xs text-muted-foreground sm:block">
                Coordinación y cobertura municipal
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <OfflineCenter />
            {permissions.includes("notifications.read") ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Notificaciones"
                asChild
              >
                <NavLink to="/notificaciones">
                  <Bell />
                </NavLink>
              </Button>
            ) : null}
            <div className="grid size-9 place-items-center rounded-full bg-accent text-xs font-bold text-primary">
              {initials}
            </div>
            <div className="hidden lg:block">
              <b className="block max-w-40 truncate text-xs">
                {user?.fullName}
              </b>
              <span className="block max-w-40 truncate text-[10px] text-muted-foreground">
                {user?.roles[0]?.areaName}
              </span>
            </div>
            {session.data?.mode === "required" ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Cerrar sesión"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                <LogOut />
              </Button>
            ) : null}
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
